import React from 'react';
import {motion} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import BrandFooter from './BrandFooter';
import TrustSection from './TrustSection';
import * as FiIcons from 'react-icons/fi';

const {FiShield,FiHeart,FiUsers,FiTrendingUp,FiCheckCircle,FiArrowRight,FiClock,FiAlertTriangle,FiPlay}=FiIcons;

const LandingPage=()=> {
  const navigate=useNavigate();

  const painPoints=[ 
    {icon: FiAlertTriangle,title: "Every Day You Wait",description: "Your family remains unprotected while you postpone this crucial decision"},{icon: FiClock,title: "Confusion Costs Time",description: "Overwhelmed by options? Not knowing what coverage you need keeps you stuck"},{icon: FiHeart,title: "The Weight of Responsibility",description: "You know you should have life insurance,but don't know where to start"} 
  ];

  const benefits=[ 
    {icon: FiCheckCircle,title: "Finally Understand Your Needs",description: "Get clear on exactly what coverage makes sense for your situation"},{icon: FiShield,title: "Stop Second-Guessing Yourself",description: "Confidence in your decision with personalized recommendations"},{icon: FiUsers,title: "Protect What You've Built",description: "Ensure your family can maintain their lifestyle without you"},{icon: FiTrendingUp,title: "Peace of Mind Today",description: "Sleep better knowing you've fulfilled your duty to protect your family"} 
  ];

  const stats=[ 
    {number: "73%",label: "of families are underinsured"},{number: "5 min",label: "to understand your needs"},{number: "$0",label: "cost to get clarity"} 
  ];

  return ( 
    <div className="min-h-screen"> 
      {/* Hero Section */} 
      <motion.section initial={{opacity: 0}} animate={{opacity: 1}} transition={{duration: 0.8}} className="relative overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white" > 
        <div className="absolute inset-0 bg-black/20"></div> 
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32"> 
          <div className="text-center"> 
            <motion.div initial={{y: 30,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{delay: 0.2,duration: 0.8}} className="mb-6" > 
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-red-500/20 backdrop-blur-sm text-sm font-medium border border-red-400/30"> 
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mr-2" /> 
                Your Family Depends on You 
              </span> 
            </motion.div> 
            <motion.h1 initial={{y: 30,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{delay: 0.3,duration: 0.8}} className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight" > 
              Stop Putting Off 
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-accent-500 drop-shadow-lg"> 
                Life Insurance 
              </span> 
            </motion.h1> 
            <motion.p initial={{y: 30,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{delay: 0.4,duration: 0.8}} className="text-xl md:text-2xl mb-10 text-blue-100 max-w-3xl mx-auto leading-relaxed" > 
              You know you need it. You've been meaning to get it. But you're stuck not knowing what type or how much coverage to buy. Let us help you figure it out in 5 minutes. 
            </motion.p> 
            <motion.div initial={{y: 30,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{delay: 0.5,duration: 0.8}} className="flex flex-col sm:flex-row gap-4 justify-center items-center" > 
              <button onClick={()=> navigate('/questionnaire')} className="group bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-700 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center" > 
                Find Out What You Need 
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" /> 
              </button> 
              <p className="text-blue-200 text-sm"> 
                ✓ No Sales Pressure ✓ Clear Recommendations ✓ Take Action Today 
              </p> 
            </motion.div> 
          </div> 
        </div> 

        {/* Stats Section */} 
        <motion.div initial={{y: 50,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{delay: 0.6,duration: 0.8}} className="relative bg-white/10 backdrop-blur-sm border-t border-white/20" > 
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"> 
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center"> 
              {stats.map((stat,index)=> ( 
                <motion.div key={index} initial={{scale: 0.8,opacity: 0}} animate={{scale: 1,opacity: 1}} transition={{delay: 0.7 + index * 0.1,duration: 0.5}} > 
                  <div className="text-3xl md:text-4xl font-bold text-accent-400 mb-2"> 
                    {stat.number} 
                  </div> 
                  <div className="text-blue-200"> 
                    {stat.label} 
                  </div> 
                </motion.div> 
              ))} 
            </div> 
          </div> 
        </motion.div> 
      </motion.section> 

      {/* Trust Section */} 
      <TrustSection /> 

      {/* Educational Video Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiPlay} className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Not Sure Which Insurance Product is Right for You?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Watch this quick educational video to understand the different types of life insurance and which might be the best fit for your situation.
            </p>
          </motion.div>

          <motion.div
            initial={{opacity: 0,scale: 0.95}}
            whileInView={{opacity: 1,scale: 1}}
            transition={{delay: 0.2,duration: 0.8}}
            viewport={{once: true}}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
              <div className="aspect-video rounded-xl overflow-hidden bg-gray-100">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/qIo_x5B9Kdk"
                  title="Life Insurance Education Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
              <div className="mt-6 text-center">
                <p className="text-gray-600 mb-4">
                  <strong>Perfect for beginners:</strong> Learn about term life, whole life, and universal life insurance in simple terms.
                </p>
                <div className="flex flex-wrap justify-center gap-3 text-sm">
                  <span className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full">✓ Term vs Permanent</span>
                  <span className="bg-accent-100 text-accent-800 px-3 py-1 rounded-full">✓ Coverage Amounts</span>
                  <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full">✓ Cost Factors</span>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">✓ When to Buy</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{opacity: 0,y: 20}}
            whileInView={{opacity: 1,y: 0}}
            transition={{delay: 0.4,duration: 0.6}}
            viewport={{once: true}}
            className="text-center mt-8"
          >
            <button
              onClick={() => navigate('/questionnaire')}
              className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center mx-auto"
            >
              Ready? Get My Personal Recommendations
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Pain Points Section */} 
      <section className="py-20 bg-gradient-to-br from-red-50 to-orange-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <motion.div initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}} className="text-center mb-16" > 
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"> 
              You're Not Alone in This Struggle 
            </h2> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto"> 
              Millions of people know they need life insurance but feel overwhelmed by the decisions. The longer you wait,the more this weighs on you. 
            </p> 
          </motion.div> 
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"> 
            {painPoints.map((point,index)=> ( 
              <motion.div key={index} initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{delay: index * 0.1,duration: 0.6}} viewport={{once: true}} className="text-center p-6 rounded-2xl bg-white border-2 border-red-100 hover:border-red-200 transition-all duration-300" > 
                <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4"> 
                  <SafeIcon icon={point.icon} className="w-8 h-8 text-white" /> 
                </div> 
                <h3 className="text-xl font-bold text-gray-900 mb-3"> 
                  {point.title} 
                </h3> 
                <p className="text-gray-600"> 
                  {point.description} 
                </p> 
              </motion.div> 
            ))} 
          </div> 
          <motion.div initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}} className="text-center" > 
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-accent-200"> 
              <h3 className="text-2xl font-bold text-gray-900 mb-4"> 
                What if something happened to you tomorrow? 
              </h3> 
              <p className="text-lg text-gray-600 mb-6"> 
                Your family would face financial hardship on top of emotional pain. You can prevent this. 
              </p> 
              <button onClick={()=> navigate('/questionnaire')} className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg" > 
                Stop Waiting,Start Protecting 
              </button> 
            </div> 
          </motion.div> 
        </div> 
      </section> 

      {/* Solution Section */} 
      <section className="py-20 bg-gradient-to-br from-orange-50 to-yellow-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <motion.div initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}} className="text-center mb-16" > 
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"> 
              Finally Get the Clarity You Need 
            </h2> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto"> 
              Our simple questionnaire cuts through the confusion and gives you personalized recommendations based on your specific situation. 
            </p> 
          </motion.div> 
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"> 
            {benefits.map((benefit,index)=> ( 
              <motion.div key={index} initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{delay: index * 0.1,duration: 0.6}} viewport={{once: true}} className="text-center p-6 rounded-2xl bg-white hover:shadow-lg transition-all duration-300" > 
                <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4"> 
                  <SafeIcon icon={benefit.icon} className="w-8 h-8 text-white" /> 
                </div> 
                <h3 className="text-xl font-bold text-gray-900 mb-3"> 
                  {benefit.title} 
                </h3> 
                <p className="text-gray-600"> 
                  {benefit.description} 
                </p> 
              </motion.div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Final CTA Section */} 
      <section className="py-20 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <motion.div initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}} className="text-center" > 
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8"> 
              Your Family's Financial Security Starts Here 
            </h2> 
            <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-2xl p-8 mb-8 border-2 border-primary-200"> 
              <h3 className="text-2xl font-bold text-gray-900 mb-4"> 
                Stop carrying this burden alone 
              </h3> 
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto"> 
                In just 5 minutes,you'll know exactly what coverage you need and how to get it. No more confusion. No more delays. Just clear direction and peace of mind. 
              </p> 
              <motion.button onClick={()=> navigate('/questionnaire')} whileHover={{scale: 1.05}} whileTap={{scale: 0.95}} className="bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-700 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 shadow-xl flex items-center mx-auto mb-6" > 
                Get My Personal Recommendations 
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" /> 
              </motion.button> 
              <p className="text-sm text-gray-500"> 
                ✓ Personalized for your situation ✓ No obligation ✓ Instant results 
              </p> 
            </div> 
            {/* Brand Footer */} 
            <BrandFooter variant="light" /> 
          </motion.div> 
        </div> 
      </section> 
    </div> 
  );
};

export default LandingPage;